import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuthStatus, useAuthToken } from "@/contexts/OptimizedAuthContext";
import { ensureAuthenticated, fetchFlags } from "@/services/api";
import {
  ALL_FLAGS_OFF,
  type FeatureFlag,
  type FlagMap,
} from "../../../shared/featureFlags";
import NotFound from "@/components/error/NotFound";

// Environment feature flags — the dark-launch layer ABOVE entitlements. A flag
// that is OFF hides the surface in this environment entirely (regardless of plan).
// Values come from Cloudflare Flagship via GET /api/v1/flags. FAIL-CLOSED: until
// the fetch resolves (or if it fails), every flag reads false (hidden), so an
// in-flux feature never flashes into view. The Worker is the authoritative gate.

interface FlagsContextValue {
  flags: FlagMap;
  loading: boolean;
}

const FlagsContext = createContext<FlagsContextValue | null>(null);

export function FlagsProvider({ children }: { children: ReactNode }) {
  const { isSignedIn, isLoaded } = useAuthStatus();
  const getToken = useAuthToken();

  const { data, isLoading } = useQuery({
    queryKey: ["flags"],
    enabled: isLoaded && isSignedIn,
    refetchOnWindowFocus: true,
    staleTime: 60_000,
    queryFn: async () => {
      await ensureAuthenticated(getToken);
      return fetchFlags();
    },
  });

  const value = useMemo<FlagsContextValue>(
    () => ({ flags: data ?? ALL_FLAGS_OFF, loading: isLoading }),
    [data, isLoading],
  );

  return (
    <FlagsContext.Provider value={value}>{children}</FlagsContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useFlags(): FlagsContextValue {
  const ctx = useContext(FlagsContext);
  if (!ctx) throw new Error("useFlags must be used within a FlagsProvider");
  return ctx;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useFlag(flag: FeatureFlag): boolean {
  return useFlags().flags[flag] === true;
}

/**
 * Route guard: 404 the surface when its environment flag is OFF, so typing the URL
 * (e.g. /analytics) in an env where it isn't shipped looks nonexistent rather than
 * upsell-able. Renders nothing while flags load (brief) to avoid a NotFound flash.
 * Compose ABOVE RequireFeature (the entitlement gate) for Pro surfaces.
 */
export function RequireFlag({
  flag,
  children,
}: {
  flag: FeatureFlag;
  children: ReactNode;
}) {
  const { flags, loading } = useFlags();
  if (loading) return null;
  if (!flags[flag]) return <NotFound />;
  return <>{children}</>;
}
